import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ChatEntryWithSubTables } from "../../../shared/aiSdkMessages";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import {
  approvePlan,
  askQuestion,
  bash,
  type ChatTool,
  connectToApp,
  editFile,
  execute,
  readFile,
  useSkill,
  webFetch,
  webSearch,
  writeFile,
} from "../../tools";
import { Chat, type ChatProps } from "../Chat";
import { ChatMessageNav } from "../Chat/ChatMessageNav";
import { ImportWorkflowModal } from "../ImportWorkflowModal";
import { readOnlyStyle } from "./readOnlyStyle";
import { ChatActionsMenu } from "./ChatActionsMenu";
import { DeriveWorkflowButton } from "./DeriveWorkflowButton";
import { PersonalChatConnectorsBar } from "./PersonalChatConnectorsBar";
import { PersonalChatVolumesButton } from "./PersonalChatVolumesButton";
import { chatSuggestions } from "./chatSlashSuggestions";
import { greetingName, NewChatEmptyState } from "./NewChatEmptyState";
import { pickChatIconColor } from "./pickChatIconColor";
import { useQuery } from "../../hooks/useQuery";
import { useRecapTrigger } from "../../hooks/useRecapTrigger";

type ChatRow = {
  id: string;
  respondingAt: number | null;
  awaitingAction: boolean | null;
  entries: readonly NonNullable<
    Parameters<typeof Chat>[0]["entries"]
  >[number][];
};

type Props = {
  personalChatId: string;
  chat: ChatRow;
  pending?: boolean;
  readOnly?: boolean;
  onForkChat?: (() => void) | undefined;
  onDownloadChat?: (() => void) | undefined;
  onCloseChat?: (() => void) | undefined;
  onTogglePin?: (() => void) | undefined;
  pinned?: boolean;
};

const Container = styled("div")`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding-bottom: 32px;
  @media (max-width: 768px) {
    padding-bottom: 8px;
  }
`;

const ScrollArea = styled("div")`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ChatWrap = styled("div")`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  max-width: 820px;
  width: 100%;
  margin: 0 auto;
  padding: 0 32px;
  box-sizing: border-box;
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const NavWrap = styled("div")`
  position: absolute;
  top: 0;
  bottom: 32px;
  right: 14px;
  width: 20px;
  pointer-events: none;
`;

const RightControls = styled("div")`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const AppsShell = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 0 4px;
  border-radius: 9999px;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  box-shadow: var(--elevation-low);
  box-sizing: border-box;
`;

export function ChatWorkspace({
  personalChatId,
  chat,
  pending,
  readOnly,
  onForkChat,
  onDownloadChat,
  onCloseChat,
  onTogglePin,
  pinned,
}: Props) {
  const z = useZero();
  const [currentUser] = useQuery(queries.currentUser());
  const userName = greetingName(currentUser?.name ?? "");
  const [pendingZip, setPendingZip] = useState<File | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  useRecapTrigger({
    chatId: chat.id,
    entries: chat.entries,
    responding: chat.respondingAt != null,
    active: !readOnly && !pending,
  });
  const tools: readonly ChatTool[] = useMemo(
    () => [
      askQuestion,
      approvePlan,
      bash({ getStepColor: () => undefined }),
      connectToApp({ personalChatId }),
      editFile({ getStepColor: () => undefined }),
      execute,
      readFile({ getStepColor: () => undefined }),
      useSkill,
      webFetch,
      webSearch,
      writeFile({ getStepColor: () => undefined }),
    ],
    [personalChatId]
  );

  useEffect(() => {
    if (pending) return;
    z.mutate(sharedMutators.touchPersonalChat({ id: personalChatId }));
  }, [personalChatId, pending, z]);

  const handleSend: ChatProps["onSend"] = (payload, onError) => {
    const result = z.mutate(
      sharedMutators.sendPersonalChatMessage({
        chatId: chat.id,
        entryId: nanoid(12),
        text: payload.text,
        isDictated: payload.isDictated,
        planMode: payload.planMode,
        effort: payload.effort,
        chatModel: payload.chatModel,
        attachments: payload.attachments,
        interruptEntryId: payload.interruptEntryId,
        clientSentAt: Date.now(),
        clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
    );
    void result.server.then((r) => {
      if (r.type === "error") onError();
    });
  };

  const handleInterrupt = () => {
    z.mutate(
      sharedMutators.interruptPersonalChat({
        entryId: nanoid(12),
        chatId: chat.id,
      })
    );
  };

  const handleProvideToolResult: ChatProps["onProvideToolResult"] = (
    entryId,
    output,
    isError,
    options
  ) => {
    z.mutate(
      sharedMutators.providePersonalChatToolResult({
        chatId: chat.id,
        entryId,
        output,
        isError,
        pauseAfter: options?.pauseAfter,
        chatModel: options?.chatModel,
      })
    );
  };

  const chatStarted = chat.entries.length > 0;

  return (
    <Container ref={containerRef}>
      <RightControls style={readOnly ? readOnlyStyle : undefined}>
        <DeriveWorkflowButton personalChatId={personalChatId} />
        {chatStarted && (
          <AppsShell>
            <PersonalChatConnectorsBar personalChatId={personalChatId} />
            <ChatActionsMenu
              onFork={onForkChat}
              onDownload={onDownloadChat}
              onClose={onCloseChat}
              onTogglePin={onTogglePin}
              pinned={pinned}
            />
          </AppsShell>
        )}
        <PersonalChatVolumesButton />
      </RightControls>
      <ScrollArea ref={scrollRef}>
        <ChatWrap>
          <Chat
            key={chat.id}
            modelScope={{ type: "personal" }}
            onZipFile={setPendingZip}
            entries={chat.entries}
            writeChatId={chat.id}
            draftKey={personalChatId}
            onSend={handleSend}
            onInterrupt={handleInterrupt}
            onProvideToolResult={handleProvideToolResult}
            isCurrentUserChat={!readOnly}
            canWrite={!readOnly}
            tools={tools}
            suggestions={chatSuggestions}
            placeholder="Ask anything across your tools and data"
            emptyState={({ fillInput, visible }) => (
              <NewChatEmptyState
                onPick={fillInput}
                iconColor={`var(--${pickChatIconColor(personalChatId)}-600)`}
                userName={userName}
                seed={personalChatId}
                visible={visible}
              />
            )}
            soundsEnabled={!readOnly}
            respondingAt={chat.respondingAt}
            awaitingAction={chat.awaitingAction ?? false}
            outerScroll
            outerScrollRef={scrollRef}
          />
          {pendingZip && (
            <ImportWorkflowModal
              file={pendingZip}
              onClose={() => setPendingZip(null)}
            />
          )}
        </ChatWrap>
      </ScrollArea>
      <NavWrap>
        <ChatMessageNav
          entries={chat.entries as ChatEntryWithSubTables[]}
          scrollContainer={scrollRef}
          wrapperRef={containerRef}
        />
      </NavWrap>
    </Container>
  );
}
